import { ErrorMonitoringService } from "../ErrorMonitoringService";

import {
  CollectionViewingContext,
  DatasetViewingContext,
  DeviceViewingContext,
  SessionViewingContext,
  ResourceTableViewingContext,
  FileViewingContext,
  ViewingContext,
  ViewingContextMap,
  ViewingContextSourceName,
  VisualizerViewingContext,
} from "./ViewingContext";

type OnChangeFn = (viewingContext: ViewingContext) => void;
type UnsubscribeFn = () => void;

interface StoredViewingContextPart<T extends ViewingContextSourceName> {
  name: T;
  value: Partial<ViewingContextMap[T]>;
}

// Union type for storage - allows storing any valid name/value pair
type AnyStoredViewingContextPart = {
  [K in ViewingContextSourceName]: StoredViewingContextPart<K>;
}[ViewingContextSourceName];

export class ViewingContextService {
  private static instance: ViewingContextService | null = null;
  private subscribers: Set<OnChangeFn> = new Set();
  private viewingContextParts: Map<symbol, AnyStoredViewingContextPart> =
    new Map();
  private cachedContext: ViewingContext | null = null;

  private constructor() {
    // Private to enforce singleton
  }

  public static getInstance(): ViewingContextService {
    if (ViewingContextService.instance === null) {
      ViewingContextService.instance = new ViewingContextService();
    }
    return ViewingContextService.instance;
  }

  // This is used by syncExternalStore thus the setTimeout/setInterval/useEffect style
  // "return an unmount function" pattern.
  public subscribe(subscriber: OnChangeFn): UnsubscribeFn {
    this.subscribers.add(subscriber);

    return () => {
      this.subscribers.delete(subscriber);
    };
  }

  public addViewingContextPart<T extends ViewingContextSourceName>(
    componentId: symbol,
    name: T,
    value: Partial<ViewingContextMap[T]>,
  ): void {
    this.viewingContextParts.set(componentId, {
      name,
      value,
    } as AnyStoredViewingContextPart);
    this.recomputeViewingContextAndNotify();
  }

  public removeViewingContextPart(componentId: symbol): void {
    if (this.viewingContextParts.has(componentId)) {
      this.viewingContextParts.delete(componentId);
    }
    this.recomputeViewingContextAndNotify();
  }

  public getViewingContext(): ViewingContext {
    if (this.cachedContext === null) {
      this.cachedContext = this.computeViewingContext();
    }
    return this.cachedContext;
  }

  /**
   * The org that owns whatever is currently being viewed, or `null` when it
   * can't be resolved to exactly one org.
   *
   * Resolution dedupes the `orgId` of each registered part (not the merged
   * context), so it is independent of how a route's parts compose: parts with
   * no `orgId` (a still-loading record, a selected-files part) contribute
   * nothing; parts that agree yield that org; parts that disagree yield `null`.
   * The visualizer collapses its own multi-source ambiguity to one org or
   * `undefined` before registering (see `singleOrgIdForDataSources` in the
   * Workspace), so the read only ever sees one org per part. Consumers (e.g.
   * CheckOutOrgButton)
   * use the single, unambiguous result to offer switching into another org.
   */
  public getViewingOrgId(): string | null {
    const orgIds = new Set<string>();
    for (const part of this.viewingContextParts.values()) {
      if (part.value.orgId) {
        orgIds.add(part.value.orgId);
      }
    }
    return orgIds.size === 1 ? [...orgIds][0] : null;
  }

  private computeViewingContext(): ViewingContext {
    const workingContext: ViewingContext = {};

    // Group parts by name and merge them
    const datasetParts: Partial<DatasetViewingContext>[] = [];
    const visualizerParts: Partial<VisualizerViewingContext>[] = [];
    const fileParts: Partial<FileViewingContext>[] = [];
    const collectionParts: Partial<CollectionViewingContext>[] = [];
    const deviceParts: Partial<DeviceViewingContext>[] = [];
    const sessionParts: Partial<SessionViewingContext>[] = [];
    const resourceTableParts: Partial<ResourceTableViewingContext>[] = [];

    for (const part of this.viewingContextParts.values()) {
      if (part.name === "dataset") {
        datasetParts.push(part.value);
      } else if (part.name === "file") {
        fileParts.push(part.value);
      } else if (part.name === "visualizer") {
        visualizerParts.push(part.value);
      } else if (part.name === "collection") {
        collectionParts.push(part.value);
      } else if (part.name === "device") {
        deviceParts.push(part.value);
      } else if (part.name === "session") {
        sessionParts.push(part.value);
      } else if (part.name === "resourceTable") {
        resourceTableParts.push(part.value);
      }
      // Org-only slots (action, invocation, ...) carry no rich shape any
      // consumer of the merged context reads; their owning org is resolved
      // straight from the raw parts in `getViewingOrgId`, so they need no
      // merged representation here.
    }

    // Merge dataset parts. At the time of writing this, selectedFiles is provided in a different place than datasetId
    if (datasetParts.length > 0) {
      const mergedDataset: DatasetViewingContext = {
        datasetId: "",
        selectedFiles: [],
      };
      for (const part of datasetParts) {
        if (part.datasetId !== undefined) {
          mergedDataset.datasetId = part.datasetId;
        }
        if (part.record !== undefined) {
          mergedDataset.record = part.record;
        }
        if (part.selectedFiles !== undefined) {
          mergedDataset.selectedFiles = part.selectedFiles;
        }
      }
      workingContext.dataset = mergedDataset;
    }

    // Take last viz parts. Very likely this should only ever be one part
    if (visualizerParts.length > 0) {
      const mergedVisualizer: VisualizerViewingContext = {
        dataSources: [],
        state: {},
      };
      for (const part of visualizerParts) {
        if (part.dataSources !== undefined) {
          mergedVisualizer.dataSources = part.dataSources;
        }
        if (part.state !== undefined) {
          mergedVisualizer.state = part.state;
        }
      }
      workingContext.visualizer = mergedVisualizer;
    }

    // Take last file parts. It's possible (but weird / unlikely) that the record is
    // provided in one place and the ID in another
    if (fileParts.length > 0) {
      const mergedFile: FileViewingContext = {
        fileId: "",
      };
      for (const part of fileParts) {
        if (part.fileId !== undefined) {
          mergedFile.fileId = part.fileId;
        }
        if (part.record !== undefined) {
          mergedFile.record = part.record;
        }
      }
      workingContext.file = mergedFile;
    }

    // Take last collection parts. Only the collection detail page registers
    // one, so in practice there is never more than a single part.
    if (collectionParts.length > 0) {
      const mergedCollection: CollectionViewingContext = {
        collectionId: "",
      };
      for (const part of collectionParts) {
        if (part.collectionId !== undefined) {
          mergedCollection.collectionId = part.collectionId;
        }
        if (part.record !== undefined) {
          mergedCollection.record = part.record;
        }
      }
      workingContext.collection = mergedCollection;
    }

    // Take last device parts. As with collections, only the device detail
    // page registers one.
    if (deviceParts.length > 0) {
      const mergedDevice: DeviceViewingContext = {
        deviceId: "",
      };
      for (const part of deviceParts) {
        if (part.deviceId !== undefined) {
          mergedDevice.deviceId = part.deviceId;
        }
      }
      workingContext.device = mergedDevice;
    }

    // Take last session parts. Only the session detail page registers them:
    // one with the id from the URL, one with the record once it loads.
    if (sessionParts.length > 0) {
      const mergedSession: SessionViewingContext = {
        sessionId: "",
      };
      for (const part of sessionParts) {
        if (part.sessionId !== undefined) {
          mergedSession.sessionId = part.sessionId;
        }
        if (part.record !== undefined) {
          mergedSession.record = part.record;
        }
      }
      workingContext.session = mergedSession;
    }

    // Take the last resource table part. A page shows one table, so there is
    // never more than one; a part missing either required field is a list
    // still mounting and says nothing yet.
    if (resourceTableParts.length > 0) {
      const last = resourceTableParts[resourceTableParts.length - 1];
      if (last.target !== undefined && last.definition !== undefined) {
        workingContext.resourceTable = {
          target: last.target,
          definition: last.definition,
          ...(last.loadedView !== undefined && { loadedView: last.loadedView }),
        };
      }
    }

    return workingContext;
  }

  private notifySubscribers(viewingContext: ViewingContext | null): void {
    if (viewingContext === null) {
      return;
    }

    this.subscribers.forEach((subscriber) => {
      try {
        subscriber(this.cachedContext!);
      } catch (error) {
        ErrorMonitoringService.captureError(error, {
          context: {
            cachedContext: this.cachedContext,
          },
        });
      }
    });
  }

  private recomputeViewingContextAndNotify(): void {
    this.cachedContext = this.computeViewingContext();
    this.notifySubscribers(this.cachedContext);
  }
}
